import { isMuseDAWBridgeDevice, isMuseRecordingDevice, isSystemAudio, isVbAudioCable } from './services/MediaDevices'
import {
    DesktopCapturerSourceExt,
    AudioSourceInfo,
    MediaSelection,
    MediaSources,
    AudioInputSettings,
    ParentRoomFragment,
    UserContextState,
} from './UserContext'
import { env } from './services/EnvironmentVariables'
import { persistent } from './services/LocalStorage'
import { AudioQualityPreset } from './services/Constants'
import * as Constants from './services/Constants'
import i18n from './i18n'
import FakeMediaStreamTrack from './core/AudioEngine/FakeMediaStreamTrack'
import { AudioEngineError, AudioEngineState } from './core/AudioEngine'
import AudioMediaSourceMatcher from './core/AudioMediaSourceMatcher'
import { RoomContextState, RoomTierPolicy } from './RoomContext'
import path from 'path'
import semver from 'semver'

export function classifyAudioInputs(audioInputs: AudioSourceInfo[]) {
    const talkbackAudioInputs: AudioSourceInfo[] = []
    const externalAudioInputs: AudioSourceInfo[] = []
    const systemAudioInputs: AudioSourceInfo[] = []
    const museDawBridge: AudioSourceInfo[] = []
    const vbAudioCable: AudioSourceInfo[] = []

    audioInputs.forEach((input) => {
        if (isSystemAudio(input)) {
            // Skip 'Muse Recording' device.
            if (isMuseRecordingDevice(input)) {
            } else {
                if (isMuseDAWBridgeDevice(input)) {
                    museDawBridge.push(input)
                } else {
                    systemAudioInputs.push(input)
                }
            }
        } else {
            externalAudioInputs.push(input)
            if (input.hostApiName !== 'ASIO') {
                talkbackAudioInputs.push(input)
            }
        }
        if (isVbAudioCable(input)) {
            vbAudioCable.push(input)
        }
    })

    systemAudioInputs.sort((a, b) => (a.label > b.label ? 1 : -1))

    if (env.TEST_NO_MUSE_KERNEL_AUDIO_DEVICES) {
        return {
            talkback: talkbackAudioInputs,
            external: externalAudioInputs,
            system: [],
            museDawBridge: [],
            vbAudioCable: [],
        }
    }

    if (env.TEST_NO_TALKBACK_AUDIO_DEVICES) {
        return {
            talkback: [],
            external: externalAudioInputs,
            system: systemAudioInputs,
            museDawBridge: museDawBridge,
            vbAudioCable: vbAudioCable,
        }
    }

    if (env.TEST_NO_EXTERNAL_AUDIO_DEVICES) {
        return {
            talkback: [],
            external: [],
            system: systemAudioInputs,
            museDawBridge: museDawBridge,
            vbAudioCable: vbAudioCable,
        }
    }

    return {
        talkback: talkbackAudioInputs,
        external: externalAudioInputs,
        system: systemAudioInputs,
        museDawBridge: museDawBridge,
        vbAudioCable: vbAudioCable,
    }
}

/**
 * This ensures that the input selectors don't select a value that doesn't exist.
 * Use this function instead of directly querying `currMediaSelection`.
 */
export function queryCurrentMediaSelectionWithFallback(
    currMediaSelection: MediaSelection,
    mediaSources: MediaSources,
    type:
        | 'desktop'
        | 'video'
        | 'audioOutput'
        | 'audioOutputMuseRecording'
        | 'midiInput'
        | 'midiOutput'
        | 'midiPassthroughOutput'
): string {
    if (currMediaSelection[type] === '_none' || currMediaSelection[type] === '_undef') {
        return currMediaSelection[type]
    }

    if (type === 'audioOutput') {
        return (
            (mediaSources.audioOutputs.filter((a) => a.deviceId === currMediaSelection.audioOutput)[0] || {})
                .deviceId || '_undef'
        )
    } else if (type === 'audioOutputMuseRecording') {
        return (
            (
                mediaSources.audioOutputsAll.filter(
                    (a) => a.deviceId === currMediaSelection.audioOutputMuseRecording
                )[0] || {}
            ).deviceId || '_undef'
        )
    } else if (type === 'desktop') {
        console.log(`queryCurrentMediaSelectionWithFallback: mediaSources.desktop=%O`, mediaSources.desktop)
        console.log(`queryCurrentMediaSelectionWithFallback: currMediaSelection.desktop=${currMediaSelection.desktop}`)
        const r = (mediaSources.desktop.filter((a) => a.id === currMediaSelection.desktop)[0] || {}).id || '_undef'
        console.log(`queryCurrentMediaSelectionWithFallback: returning ${r}`)
        return r
    } else if (type === 'video') {
        return (mediaSources.video.filter((a) => a.deviceId === currMediaSelection.video)[0] || {}).deviceId || '_undef'
    } else if (type === 'midiInput') {
        return (mediaSources.midiInputs.filter((a) => a.id === currMediaSelection.midiInput)[0] || {}).id || '_undef'
    } else if (type === 'midiOutput') {
        return (mediaSources.midiOutputs.filter((a) => a.id === currMediaSelection.midiOutput)[0] || {}).id || '_undef'
    } else if (type === 'midiPassthroughOutput') {
        return (
            (mediaSources.midiOutputs.filter((a) => a.id === currMediaSelection.midiPassthroughOutput)[0] || {}).id ||
            '_undef'
        )
    } else {
        return '_undef'
    }
}

export function getAuthTokenFromLocalUserCache(): string | null {
    const authToken = (persistent.getObject('userCache') || {}).authToken || null
    if (authToken == null) {
        return null
    } else {
        if (typeof authToken !== 'string' || authToken.trim().length === 0) {
            localStorage.removeItem('userCache')
            return null
        } else {
            return authToken
        }
    }
}

export function makeTalkbackKeyDescriptionString(keyObj: any) {
    if (keyObj && keyObj.browser) {
        if (keyObj.characterRepresentation && keyObj.characterRepresentation !== keyObj.browser) {
            return `${keyObj.browser} (${keyObj.characterRepresentation})`
        } else {
            return keyObj.browser
        }
    } else {
        return '(none)'
    }
}

export function chooseFirstScreen(desktops: DesktopCapturerSourceExt[]): DesktopCapturerSourceExt | null {
    const fullDesktopsOnly = desktops.filter((d) => d.screenIndex != null)

    if (fullDesktopsOnly.length > 0) {
        // @ts-ignore
        fullDesktopsOnly.sort((a, b) => a.screenIndex - b.screenIndex)

        var screen = fullDesktopsOnly[0]
    } else {
        // as a last resort
        var screen = desktops[0] || null
    }

    return screen
}

export function isDefinedDeviceId(id: string | null | undefined): boolean {
    if (!id) {
        return false
    }

    if (id === '_undef' || id === '_none') {
        return false
    }

    return true
}

export function encodeExtendedDeviceId(deviceId: string, chgrp: number[]) {
    return `${deviceId}?chgrp=${JSON.stringify(chgrp)}`
}

function parseQueryParams(queryString: string) {
    const params: any = {}
    // Remove the part before the '?' if present
    const query = queryString.split('?')[1] || queryString
    // Split the query into individual key-value pairs
    const pairs = query.split('&')
    for (let pair of pairs) {
        const [key, value] = pair.split('=')
        // Decode URI components and assign to the params object
        if (key) {
            params[key] = value || ''
        }
    }
    return params
}

export function decodeExtendedDeviceId(extendedDeviceId: string) {
    const qMarkerIdx = extendedDeviceId.indexOf('?')
    if (qMarkerIdx) {
        const deviceId = extendedDeviceId.substring(0, qMarkerIdx)
        const params = parseQueryParams(extendedDeviceId)

        const chgrp: number[] = (() => {
            try {
                return JSON.parse(params.chgrp)
            } catch (e) {
                return undefined
            }
        })()

        return {
            deviceId,
            ...params,
            chgrp,
        }
    } else {
        return { deviceId: extendedDeviceId }
    }
}

export function getDefaultAudioDevicePreset(audioSource: AudioSourceInfo, hint?: { isTalkbackMic?: boolean }) {
    if (hint?.isTalkbackMic) {
        return Constants.getAudioQualityPresets().find((a) => a.code === 'voice')
    }

    if (audioSource.hostApiName === 'ASIO') {
        return Constants.getAudioQualityPresets().find((a) => a.code === 'pro_essential')
    }

    if (audioSource.isSystemAudio) {
        return Constants.getAudioQualityPresets().find((a) => a.code === 'enhanced')
    }

    return Constants.getAudioQualityPresets().find((a) => a.code === 'voice')
}

export type AudioEngineRemoteStreamError = {
    code:
        | 'audio_engine_output_device_error'
        | 'audio_engine_high_audio_dropout'
        | 'audio_engine_connection_error'
        | 'audio_engine_not_supported_error'
    message: string
    messageDetails: string
    severity: 'warning' | 'error'
}
export function getAudioEngineRemoteDeviceError(
    stateMediaStreamsAudioEngineAudioOutputError: AudioEngineError | null,
    track: FakeMediaStreamTrack
): AudioEngineRemoteStreamError | null {
    const remoteStreamStats = track.remoteStats

    if (stateMediaStreamsAudioEngineAudioOutputError) {
        let err = stateMediaStreamsAudioEngineAudioOutputError
        return {
            code: 'audio_engine_output_device_error',
            message:
                `Failed to set Pro Audio speaker device.\nPlease contact support for assistance.\n` +
                `Details: "${err?.debugMessage}" ` +
                `(${err?.code})`,
            messageDetails: '',
            severity: 'error',
        }
    }

    const refractoryPeriod = 3000
    const dtms = new Date().getTime() - track.startedTime.getTime()
    if (dtms < refractoryPeriod) {
        return null
    }

    if ((remoteStreamStats?.pctLostAudio3s ?? 0) >= 100 && remoteStreamStats?.connectionState === 'CONNECTED') {
        return {
            code: 'audio_engine_high_audio_dropout',
            message:
                `Looks like we are not receiving audio from this device. To mitigate ` +
                `this issue, the owner of this stream can try deselecting and selecting the device again, ` +
                `or restart the app.`,
            messageDetails: '',
            severity: 'error',
        }
    }

    if (
        (remoteStreamStats?.pctLostAudio3s ?? 0) > Constants.data.audioengine_dropped_audio_warning_pct_threshold &&
        remoteStreamStats?.connectionState === 'CONNECTED'
    ) {
        return {
            code: 'audio_engine_high_audio_dropout',
            message:
                `High audio dropout detected. Please try decreasing the audio quality or ` +
                `increasing the buffer size.`,
            messageDetails: '',
            severity: 'warning',
        }
    }

    if ((remoteStreamStats?.connectionState || '').length > 0 && remoteStreamStats?.connectionState !== 'CONNECTED') {
        return {
            code: 'audio_engine_connection_error',
            message: `We're having trouble connecting to the Pro Audio server.`,
            messageDetails:
                `You may have trouble connecting to the Pro Audio server if you are ` +
                `on a restrictive network, such as corporate or school network.\n` +
                `We are actively working to improve Pro Audio network compatibility.`,
            severity: 'error',
        }
    }

    return null
}

export function createCustomJoinLink(customJoinCode: string | null) {
    let appUrlParam = ''
    if (customJoinCode) {
        for (let link of Constants.app_links) {
            if (!link.default && semver.satisfies(env.APP_VERSION, link.version_range)) {
                appUrlParam = `?app_url=${btoa(link.app_url)}`
            }
        }
        return `${Constants.links.website_index}/session${appUrlParam}#${customJoinCode}`
    } else {
        return null
    }
}

export function getSessionJoinLink(currentRoom: {
    customJoinLink: string | null
    joinLink: string | null
    parentRoom: ParentRoomFragment | null
}) {
    if (currentRoom.parentRoom) {
        if (currentRoom.parentRoom.customJoinCode) {
            return createCustomJoinLink(currentRoom.parentRoom.customJoinCode) || '?'
        }
        return currentRoom.parentRoom.joinLink || '?'
    }
    return currentRoom.customJoinLink || currentRoom.joinLink || '?'
}

export function getListenOnlySessionLink(roomState: { anonymousId: string }) {
    if (Constants.app_links?.length > 0) {
        for (const versionedAppLink of Constants.app_links) {
            if (semver.satisfies(env.APP_VERSION, versionedAppLink.version_range)) {
                return `${versionedAppLink.app_url}/guest-join?clientType=listenonly&code=${roomState.anonymousId}`
            }
        }
    }

    return `${Constants.links.webapp_index}/guest-join?clientType=listenonly&code=${roomState.anonymousId}`
}

export const audioMediaSourceMatcher = new AudioMediaSourceMatcher()

export function isSidebarRetractedOnStart(state: UserContextState, roomState: RoomContextState) {
    try {
        if (state.clientType === 'listenonly') {
            if (roomState.roomTierPolicy.allowAudienceChat) {
                return false
            } else {
                return true
            }
        } else {
            return false
        }
    } catch (e) {
        return false
    }
}
